<template>
  <div :class="isFocused ? 'ring-3' : ''">
    <div :draggable="draggable">
      <LazyImage
        ref="imageRef"
        :class="isFocused ? 'focused' : undefined"
        :src="src"
        :alt-text="altText"
        :width="width"
        :height="height"
        :max-width="maxWidth"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { NodeKey, NodeSelection, RangeSelection } from "lexical";
import {
  $getNodeByKey,
  $getSelection,
  $isNodeSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_LOW,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
} from "lexical";
import { mergeRegister } from "@lexical/utils";
import { useLexicalComposer, useLexicalNodeSelection } from "lexical-vue";
import { computed, onMounted, onUnmounted, ref } from "vue";
// import ImageResizer from "./ImageResizer.vue";
import { $isImageNode, LazyImage } from "./ImageNode";

// Define props for the component
const props = defineProps<{
  src: string;
  altText: string;
  nodeKey: NodeKey;
  width: "inherit" | number;
  height: "inherit" | number;
  maxWidth: number;
  resizable: boolean;
}>();

// Component state
const imageRef = ref<InstanceType<typeof LazyImage>>();
const selection = ref<RangeSelection | NodeSelection | null>(null);
const isResizing = ref(false);

// Context hooks
const editor = useLexicalComposer();
const { isSelected, setSelected, clearSelection } = useLexicalNodeSelection(
  props.nodeKey
);

// Computed properties
const draggable = computed(() => isSelected.value && $isNodeSelection(selection.value));
const isFocused = computed(
  () => $isNodeSelection(selection.value) && (isSelected.value || isResizing.value)
);

const onDelete = (payload: KeyboardEvent) => {
  if (isSelected.value && $isNodeSelection($getSelection())) {
    const event: KeyboardEvent = payload;
    event.preventDefault();
    const node = $getNodeByKey(props.nodeKey);
    if ($isImageNode(node)) {
      node.remove();
    }
    setSelected(false);
  }
  return false;
};

onBeforeMount(() => {
  console.log("ImageNodeRenderer mounted");
});

// Lifecycle hooks
onMounted(() => {
  const unregister = mergeRegister(
    editor.registerUpdateListener(({ editorState }: { editorState: any }) => {
      editorState.read(() => {
        selection.value = $getSelection() as RangeSelection | NodeSelection | null;
      });
    }),
    editor.registerCommand<MouseEvent>(
      CLICK_COMMAND,
      (payload: MouseEvent) => {
        const event = payload;

        if (isResizing.value) {
          return true;
        }
        if (event.target && event.target === imageRef.value?.$el) {
          if (!event.shiftKey) {
            clearSelection();
          }
          setSelected(!isSelected.value);
          return true;
        }

        return false;
      },
      COMMAND_PRIORITY_LOW
    ),
    editor.registerCommand(KEY_DELETE_COMMAND, onDelete, COMMAND_PRIORITY_LOW),
    editor.registerCommand(KEY_BACKSPACE_COMMAND, onDelete, COMMAND_PRIORITY_LOW)
  );

  // Cleanup on component unmount
  onUnmounted(() => {
    unregister();
  });
});

// Using the LazyImage component imported from the ImageNode module
</script>

<style>
@import "./ImageNode.css";
</style>
